import dynamic from 'next/dynamic';
import { useMemo, useRef, useState } from 'react';
import parse from 'html-react-parser';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';

import { PolarChart } from './components/polarChart';
import { Footer } from './components/footer';
import { ShareResults } from './components/shareResults';
import { KeyResultsDialog } from './components/keyResultsDialog';

import DesignSkillStackJson from '@/json/design-skill-stack/data.json';
import { ScoringData, SubCategory } from './types';

import { withAuth } from '../../with.auth';

const ScoreCard = dynamic<any>(() =>
  import('./components/scoreCard').then((module) => module.ScoreCard)
);

const CategoryTotalWithDonutChart = dynamic<any>(() =>
  import('./components/categoryTotalWithDonutChart').then(
    (module) => module.CategoryTotalWithDonutChart
  )
);

const CategoryAccordion = dynamic<any>(() =>
  import('./components/categoryAccordion').then(
    (module) => module.CategoryAccordion
  )
);

const openGlossaryPdf = () => {
  window.open('/DSS-glossary-definitions.pdf');
};

const DesignSkillStackResults = ({
  design,
  submissionData,
  submissionId,
}: any) => {
  const [openKeyDialog, setOpenKeyDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const targetRef = useRef<HTMLDivElement>(null);

  const formatScore = (total: number, divisor: number): number => {
    return Math.round((total / divisor) * 10) / 10 || 0;
  };

  const scoringData: ScoringData[] | undefined = useMemo(() => {
    if (submissionData && DesignSkillStackJson) {
      // Merging the field/submission data and data from local JSON
      const mergedSubmissions = submissionData.submission.fields.map(
        (submission: any) => {
          const fieldFromJson = DesignSkillStackJson.questions.find(
            (question) => {
              if (question.questionId === submission.field.id) {
                return question;
              }
            }
          );

          return {
            ...submission,
            ...fieldFromJson,
          };
        }
      );

      // Removing unecessary data since there are 2 fields per slide.
      const removeDuplicates: any[] = [];
      mergedSubmissions.forEach((item: any) => {
        const findMatch = mergedSubmissions.find(
          (ms: any) => ms.slideNumber === item.slideNumber && ms.id !== item.id
        );

        if (item.experience) {
          const updated = {
            ...item,
            ...{
              experience: parseInt(item.value.replace('"', '')) || 0,
              aspiration: parseInt(findMatch.value.replace('"', '')) || 0,
            },
          };
          removeDuplicates.push(updated);
        }
      });

      // Constructing scoring data to build components on results page
      const categoryData = DesignSkillStackJson.categories.map(
        (category, categoryIndex) => {
          let subCategories: SubCategory[] = [];
          let categoryAspiration: number = 0;
          let categoryExperience: number = 0;
          if (category?.subCategories) {
            subCategories = category.subCategories.map(
              (subCategory: any, subCategoryIndex) => {
                let aspirationTotal: number = 0;
                let experienceTotal: number = 0;
                const filteredSubCategories = removeDuplicates.filter(
                  (question) => {
                    if (
                      question.category === category.id &&
                      question.subCategory === subCategory.id
                    ) {
                      aspirationTotal += question.aspiration;
                      experienceTotal += question.experience;

                      return {
                        name: question.name,
                        experience: question.experience,
                        aspiration: question.aspiration,
                      };
                    }
                  }
                );

                categoryAspiration += formatScore(
                  aspirationTotal,
                  filteredSubCategories.length
                );
                categoryExperience += formatScore(
                  experienceTotal,
                  filteredSubCategories.length
                );

                return {
                  name: subCategory.name,
                  id: `cat-${categoryIndex}-subCat-${subCategoryIndex}`,
                  questions: filteredSubCategories,
                  experience: formatScore(
                    experienceTotal,
                    filteredSubCategories.length
                  ),
                  aspiration: formatScore(
                    aspirationTotal,
                    filteredSubCategories.length
                  ),
                  experienceColors: subCategory?.experienceColors,
                  aspirationColors: subCategory?.aspirationColors,
                };
              }
            );
          }

          return {
            name: category.name,
            stackDescription: category.stackDescription,
            description: category.description,
            aspiration: formatScore(categoryAspiration, subCategories.length),
            experience: formatScore(categoryExperience, subCategories.length),
            experienceColors: category.experienceColors,
            aspirationColors: category.aspirationColors,
            subCategories: subCategories,
            id: `cat${categoryIndex}`,
          };
        }
      );

      return categoryData;
    }
  }, [submissionData]);

  const firstName: string = useMemo(() => {
    if (submissionData) {
      const field = submissionData.submission.fields.find(
        (item: any) => item.field.id === 'J7NL5V'
      );
      return field?.value.replaceAll('"', '');
    }
    return '';
  }, [submissionData]);

  const polarChartData = useMemo(() => {
    const scores: number[] = [];
    if (scoringData && scoringData[6]) {
      scoringData[6].subCategories[0].questions.map((question) => {
        scores.push(question.aspiration);
        scores.push(question.experience);
        return;
      });
    }
    return scores;
  }, [scoringData]);

  return (
    <Box ref={targetRef} sx={{ background: '#000' }}>
      {loading && (
        <Backdrop
          open={loading}
          style={{ zIndex: 999, height: '100vh', position: 'absolute' }}
        >
          <CircularProgress
            sx={{ position: 'absolute', top: '75px', color: '#fff' }}
          />
        </Backdrop>
      )}
      <Box
        sx={{
          position: 'absolute',
          top: 32,
          right: 64,
          display: 'flex',
          gap: 2,
        }}
      >
        <Button
          size='small'
          onClick={() => {
            setOpenKeyDialog(true);
          }}
          endIcon={<ArrowForwardIosIcon />}
          sx={{ color: 'white', ':hover': { backgroundColor: '#2b2b2b' } }}
        >
          Key
        </Button>
        <Button
          size='small'
          endIcon={<ArrowForwardIosIcon />}
          sx={{ color: 'white', ':hover': { backgroundColor: '#2b2b2b' } }}
          onClick={openGlossaryPdf}
        >
          Glossary
        </Button>
      </Box>
      <Grid
        container
        sx={{
          justifyContent: 'center',
        }}
      >
        <Grid
          sx={{
            marginTop: '113px',
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '1000px',
            padding: '0 30px 40px',
            justifyContent: 'center',
          }}
        >
          <KeyResultsDialog
            openKeyDialog={openKeyDialog}
            onClose={() => {
              setOpenKeyDialog(false);
            }}
          />
          <Grid item sx={{ alignItems: 'center', width: '100%' }}>
            <Typography
              sx={{
                fontSize: '43px',
                fontWeight: '700',
                color: design.colors.question,
              }}
            >
              Congrats, {firstName ? firstName : ''}!<br /> You&apos;ve
              completed the Design SkillStack survey.
            </Typography>
            <Typography
              sx={{
                fontSize: '19px',
                marginTop: '24px',
                color: design.colors.question,
              }}
            >
              Now that you&apos;ve taken inventory of your skills, where do you
              want to go? And how do you want to get there?
              <br />
              <br /> Remember, this is all about YOU! Your results are a
              baseline to see what you&apos;ve accomplished, and how much real
              world experience you have. Use this data to inform your career
              aspirations. Let this information be a springboard from which you
              and your coach set your professional goals, and measure your
              progress over time.
              <br />
              <br /> And if you&apos;re a master in a certain subject, make it
              known. Let us know so we can create opportunities for you to teach
              and mentor peers who want to learn from you.
            </Typography>
            <ShareResults
              design={design}
              submissionId={submissionId}
              targetRef={targetRef}
              setLoading={setLoading}
              showHeading={true}
              text='Share your results with your coach'
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 0 30px',
                flexDirection: 'column',
              }}
            >
              <Typography
                sx={{
                  fontSize: '43px',
                  fontWeight: '700',
                  color: '#fff',
                  marginTop: '12px',
                }}
              >
                Your Results
              </Typography>
              {/* DATES WILL BE NEEDED FOR MVP2 */}
              {/* <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#fff',
                }}
              >
                Inventory TAKEN 06/12/24
              </Typography>
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: '700',
                  color: '#A3A3A3',
                }}
              >
                PREVIOUS RESULTS 01/02/24, 06/27/23
              </Typography> */}
            </Box>
          </Grid>
        </Grid>
      </Grid>
      <Grid
        container
        sx={{
          justifyContent: 'center',
          borderBottom: '1px solid #5F5F5F',
        }}
      >
        <Grid
          sx={{
            display: 'flex',
            maxWidth: '1440px',
            padding: '0 30px 40px',
            justifySelf: 'center',
            width: '100%',
            flexDirection: 'column',
          }}
        >
          <Typography
            sx={{
              fontSize: '43px',
              fontWeight: '700',
              marginTop: '48px',
              color: '#fff',
            }}
          >
            Stack Categories
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              marginTop: '10px',
            }}
          >
            <Grid item md={4} xs={12} sx={{ margin: '5px 5px 5px 0' }}>
              {scoringData && scoringData[0] && (
                <ScoreCard
                  chartId={'1'}
                  data={[scoringData[0].aspiration, scoringData[0].experience]}
                  title={scoringData[0].name}
                  description={scoringData[0].stackDescription}
                  anchor={scoringData[0].id}
                />
              )}
            </Grid>
            <Grid item md={4} xs={12} sx={{ margin: '5px 5px 5px 0' }}>
              {scoringData && scoringData[1] && (
                <ScoreCard
                  chartId={'2'}
                  data={[scoringData[1].aspiration, scoringData[1].experience]}
                  title={scoringData[1].name}
                  description={scoringData[1].stackDescription}
                  anchor={scoringData[1].id}
                />
              )}
            </Grid>
            <Grid item md={4} xs={12} sx={{ margin: '5px 5px 5px 0' }}>
              {scoringData && scoringData[2] && (
                <ScoreCard
                  chartId={'3'}
                  data={[scoringData[2].aspiration, scoringData[2].experience]}
                  title={scoringData[2].name}
                  description={scoringData[2].stackDescription}
                  anchor={scoringData[2].id}
                />
              )}
            </Grid>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            <Grid item md={4} xs={12} sx={{ margin: '5px 5px 5px 0' }}>
              {scoringData && scoringData[3] && (
                <ScoreCard
                  chartId={'4'}
                  data={[scoringData[3].aspiration, scoringData[3].experience]}
                  title={scoringData[3].name}
                  description={scoringData[3].stackDescription}
                  anchor={scoringData[3].id}
                />
              )}{' '}
            </Grid>
            <Grid item md={4} xs={12} sx={{ margin: '5px 5px 5px 0' }}>
              {scoringData && scoringData[4] && (
                <ScoreCard
                  chartId={'5'}
                  data={[scoringData[4].aspiration, scoringData[4].experience]}
                  title={scoringData[4].name}
                  description={scoringData[4].stackDescription}
                  anchor={scoringData[4].id}
                />
              )}{' '}
            </Grid>
            <Grid item md={4} xs={12} sx={{ margin: '5px 5px 5px 0' }}>
              {scoringData && scoringData[5] && (
                <ScoreCard
                  chartId={'6'}
                  data={[scoringData[5].aspiration, scoringData[5].experience]}
                  title={scoringData[5].name}
                  description={scoringData[5].stackDescription}
                  anchor={scoringData[5].id}
                />
              )}{' '}
            </Grid>
          </Box>
        </Grid>
      </Grid>
      <Box
        sx={{
          justifyContent: 'center',
          display: 'flex',
          borderBottom: '1px solid #5F5F5F',
        }}
      >
        <Grid
          sx={{
            justifyContent: 'center',
            maxWidth: '1440px',
            padding: '20px',
          }}
          container
          spacing={2}
        >
          <Grid item xs={12} md={4}>
            <Typography
              sx={{
                fontSize: '43px',
                fontWeight: '700',
                color: '#fff',
              }}
            >
              Craft Overview
            </Typography>
            <Typography
              sx={{
                fontSize: '19px',
                color: '#fff',
              }}
            >
              Design-led Product & Engineering (DP&E) consists of practitioners
              that span across the crafts of User Experience Research (UXR),
              Design Strategy (XS), User Experience Design (UX), Visual Design
              (UI), Product Design (PD), and Extended Reality Design (XR).
              Although each of us is aligned to one discipline, most of us have
              skills from one or more of the other disciplines.
            </Typography>
            <Typography
              sx={{
                fontSize: '19px',
                color: '#fff',
                marginTop: '10px',
              }}
            >
              Here&apos;s where you lie experience and aspirations-wise.
            </Typography>
          </Grid>
          <Grid item xs={12} md={8}>
            <PolarChart
              data={polarChartData}
              labels={[
                'User Experience Research Experience',
                'User Experience Research Aspiration',
                'Visual Design Experience',
                'Visual Design Aspiration',
                'User Experience Design',
                'User Experience Design Aspiration',
                'UX Strategy Experience',
                'UX Strategy Aspiration',
                'Extended Reality Design Experience',
                'Extended Reality Design Aspiration',
                'Product Design Experience',
                'Product Design Aspiration',
              ]}
              colors={[
                'rgb(146, 186, 73, .9)',
                'rgb(213, 243, 159, .9)',
                'rgb(217, 0, 104, .9)',
                'rgb(218, 117, 166, .9)',
                'rgb(85, 77, 198, .9)',
                'rgb(150, 146, 203, .9)',
                'rgb(0, 133, 208, .9)',
                'rgb(140, 202, 238, .9)',
                'rgb(254, 93, 58, .9)',
                'rgb(246, 174, 158, .9)',
                'rgb(0, 143, 118, .9)',
                'rgb(93, 207, 186, .9)',
              ]}
            />
          </Grid>
        </Grid>
      </Box>
      {scoringData?.map((sd, i) => (
        <Box key={i}>
          {sd.id !== 'cat7' && sd.id !== 'cat6' && (
            <>
              <Box
                sx={{
                  justifyContent: 'center',
                  display: 'flex',
                }}
                id='UniversalSkills'
              >
                <Grid
                  container
                  sx={{
                    marginTop: '40px',
                    display: 'flex',
                    maxWidth: '1440px',
                    padding: '0 30px',
                    justifyContent: 'center',
                    justifySelf: 'center',
                  }}
                  id={sd.id}
                >
                  <Grid item xs={12} md={4}>
                    <Typography
                      sx={{
                        fontSize: '43px',
                        fontWeight: '700',
                        color: '#fff',
                      }}
                    >
                      {sd.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '19px',
                        color: '#fff',
                      }}
                    >
                      {parse(sd.description)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <CategoryTotalWithDonutChart
                      currentData={{
                        scores: [sd.aspiration, sd.experience],
                        colors: ['#fff', '#C4C4C4'],
                        id: 'universalTotals1',
                      }}
                      subCategories={sd.subCategories}
                    />
                  </Grid>
                </Grid>
              </Box>
              <Box
                sx={{
                  justifyContent: 'center',
                  display: 'flex',
                  borderBottom: '1px solid #5F5F5F',
                  paddingBottom: '40px',
                }}
              >
                <Grid
                  sx={{
                    marginTop: '40px',
                    display: 'flex',
                    flexDirection: 'column',
                    maxWidth: '1440px',
                    padding: '0 30px',
                    width: '100%',
                  }}
                >
                  <CategoryAccordion data={sd} />
                </Grid>
              </Box>
            </>
          )}
        </Box>
      ))}
      <Box>
        <Typography
          sx={{
            color: '#fff',
            fontSize: '44px',
            fontWeight: '700',
            textAlign: 'center',
            marginTop: '40px',
          }}
        >
          Share your results with your coach
        </Typography>
        <Typography
          sx={{
            color: '#fff',
            fontSize: '17px',
            fontWeight: '700',
            textAlign: 'center',
            marginTop: '20px',
          }}
        >
          Have a conversation about your experience and aspirations.
        </Typography>
        <ShareResults
          design={design}
          submissionId={submissionId}
          targetRef={targetRef}
          setLoading={setLoading}
          text='Share your results with your coach'
        />
      </Box>
      <Footer design={design} />
    </Box>
  );
};

export default withAuth(DesignSkillStackResults);
